import { 
  Box, 
  Container, 
  Typography, 
  Grid, 
  Card, 
  CardContent, 
  Accordion, 
  AccordionSummary, 
  AccordionDetails, 
  Paper,
  IconButton,
  Button
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import DashboardIcon from '@mui/icons-material/Dashboard';
import PeopleIcon from '@mui/icons-material/People';
import StorageIcon from '@mui/icons-material/Storage';
import CloudSyncIcon from '@mui/icons-material/CloudSync';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import FolderIcon from '@mui/icons-material/Folder';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SearchIcon from '@mui/icons-material/Search';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

const HelpSection = ({ icon, title, description, children }) => (
  <Paper elevation={0} sx={{ p: 4, mb: 4, borderRadius: 4, border: '1px solid', borderColor: 'divider' }}>
    <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, gap: 2 }}>
      <Box sx={{ p: 1.5, bgcolor: 'primary.main', color: 'white', borderRadius: 2, display: 'flex' }}>
        {icon}
      </Box>
      <Box>
        <Typography variant="h5" sx={{ fontWeight: 800 }}>{title}</Typography>
        <Typography variant="body2" color="text.secondary">{description}</Typography>
      </Box>
    </Box>
    {children}
  </Paper>
);

const HelpPage = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  return (
    <Container maxWidth="lg" sx={{ py: 6 }}>
      <Box sx={{ mb: 6, display: 'flex', alignItems: 'center', gap: 2 }}>
        <IconButton onClick={() => navigate(-1)} sx={{ bgcolor: 'action.hover' }}>
          <ArrowBackIcon />
        </IconButton>
        <Box>
          <Typography variant="h3" sx={{ fontWeight: 900, mb: 0.5, letterSpacing: -1 }}>
            {t("Help Manual")}
          </Typography>
          <Typography variant="h6" color="text.secondary" sx={{ fontWeight: 500 }}>
            {t("Help Manual Desc")}
          </Typography>
        </Box>
      </Box>

      <Grid container spacing={4}>
        <Grid item xs={12} md={4}>
          <Box sx={{ position: { md: 'sticky' }, top: 100 }}>
            <Card elevation={0} sx={{ border: '1px solid', borderColor: 'primary.light', bgcolor: 'primary.50', borderRadius: 4 }}>
              <CardContent>
                <Typography variant="subtitle1" sx={{ fontWeight: 800, mb: 2, color: 'primary.main' }}>
                  {t("Quick Navigation")}
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Button startIcon={<DashboardIcon />} sx={{ justifyContent: 'flex-start', fontWeight: 600 }}>{t("Overview")}</Button>
                  <Button startIcon={<PeopleIcon />} sx={{ justifyContent: 'flex-start', fontWeight: 600 }}>{t("Patients")}</Button>
                  <Button startIcon={<FolderIcon />} sx={{ justifyContent: 'flex-start', fontWeight: 600 }}>{t("Clinical Files")}</Button>
                  <Button startIcon={<SearchIcon />} sx={{ justifyContent: 'flex-start', fontWeight: 600 }}>{t("Query & Export")}</Button>
                  <Button startIcon={<CloudSyncIcon />} sx={{ justifyContent: 'flex-start', fontWeight: 600 }}>{t("Sync Hub")}</Button>
                  <Button startIcon={<StorageIcon />} sx={{ justifyContent: 'flex-start', fontWeight: 600 }}>{t("Migration")}</Button>
                </Box>
              </CardContent>
            </Card>

            <Box sx={{ mt: 4, textAlign: 'center', p: 3, bgcolor: 'action.hover', borderRadius: 4 }}>
              <HelpOutlineIcon color="disabled" sx={{ fontSize: 48, mb: 1, opacity: 0.5 }} />
              <Typography variant="body2" color="text.secondary">
                {t("Need extra support")}
              </Typography>
            </Box>
          </Box>
        </Grid>

        <Grid item xs={12} md={8}>
          {/* Section 1: Dashboard Overview */}
          <HelpSection 
            icon={<DashboardIcon />} 
            title={t("Surgical Operations")} 
            description={t("Surgical Operations Desc")}
          >
            <Accordion elevation={0} disableGutters sx={{ '&:before': { display: 'none' } }}>
              <AccordionSummary expandMoreIcon={<ExpandMoreIcon />}>
                <Typography sx={{ fontWeight: 700 }}>{t("Real-Time Clinical Alerts")}</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.8 }}>
                  {t("Real-Time Clinical Alerts Desc")}
                </Typography>
              </AccordionDetails>
            </Accordion>
          </HelpSection>

          {/* Section 2: Patient Records */}
          <HelpSection 
            icon={<PeopleIcon />} 
            title={t("Patient Management")} 
            description={t("Patient Management Desc")}
          >
            <Accordion elevation={0} disableGutters sx={{ '&:before': { display: 'none' } }}>
              <AccordionSummary expandMoreIcon={<ExpandMoreIcon />}>
                <Typography sx={{ fontWeight: 700 }}>{t("Recording Medical History")}</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.8 }}>
                  {t("Recording Medical History Desc")}
                </Typography>
              </AccordionDetails>
            </Accordion>
            <Accordion elevation={0} disableGutters sx={{ '&:before': { display: 'none' } }}>
              <AccordionSummary expandMoreIcon={<ExpandMoreIcon />}>
                <Typography sx={{ fontWeight: 700 }}>{t("Tracking Clinical Visits")}</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.8 }}>
                  {t("Tracking Clinical Visits Desc")}
                </Typography>
              </AccordionDetails>
            </Accordion>
            <Accordion elevation={0} disableGutters sx={{ '&:before': { display: 'none' } }}>
              <AccordionSummary expandMoreIcon={<ExpandMoreIcon />}>
                <Typography sx={{ fontWeight: 700 }}>{t("Surgical Medications")}</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.8 }}>
                  {t("Surgical Medications Desc")}
                </Typography>
              </AccordionDetails>
            </Accordion>
          </HelpSection>

          {/* Section 3: Clinical Files */}
          <HelpSection 
            icon={<FolderIcon />} 
            title={t("Digital Surgical Files")} 
            description={t("Digital Surgical Files Desc")}
          >
            <Accordion elevation={0} disableGutters sx={{ '&:before': { display: 'none' } }}>
              <AccordionSummary expandMoreIcon={<ExpandMoreIcon />}>
                <Typography sx={{ fontWeight: 700 }}>{t("Uploading Surgical Photos")}</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.8 }}>
                  {t("Uploading Surgical Photos Desc")}
                </Typography>
              </AccordionDetails>
            </Accordion>
            <Accordion elevation={0} disableGutters sx={{ '&:before': { display: 'none' } }}>
              <AccordionSummary expandMoreIcon={<ExpandMoreIcon />}>
                <Typography sx={{ fontWeight: 700 }}>{t("Scanning & Thumbnails")}</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.8 }}>
                  {t("Scanning & Thumbnails Desc")}
                </Typography>
              </AccordionDetails>
            </Accordion>
            <Accordion elevation={0} disableGutters sx={{ '&:before': { display: 'none' } }}>
              <AccordionSummary expandMoreIcon={<ExpandMoreIcon />}>
                <Typography sx={{ fontWeight: 700 }}>{t("Generating Clinical Reports")}</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.8 }}>
                  {t("Generating Clinical Reports Desc")}
                </Typography>
              </AccordionDetails>
            </Accordion>
          </HelpSection>

          {/* Section 4: Query & Export */}
          <HelpSection 
            icon={<SearchIcon />} 
            title={t("Query & Export")} 
            description={t("Query & Export Desc")}
          >
            <Accordion elevation={0} disableGutters sx={{ '&:before': { display: 'none' } }}>
              <AccordionSummary expandMoreIcon={<ExpandMoreIcon />}>
                <Typography sx={{ fontWeight: 700 }}>{t("Advanced Filtering")}</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.8 }}>
                  {t("Advanced Filtering Desc")}
                </Typography>
              </AccordionDetails>
            </Accordion>
            <Accordion elevation={0} disableGutters sx={{ '&:before': { display: 'none' } }}>
              <AccordionSummary expandMoreIcon={<ExpandMoreIcon />}>
                <Typography sx={{ fontWeight: 700 }}>{t("Excel Export")}</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.8 }}>
                  {t("Excel Export Desc")}
                </Typography>
              </AccordionDetails>
            </Accordion>
          </HelpSection>

          {/* Section 5: Sync & Hub */}
          <HelpSection 
            icon={<CloudSyncIcon />} 
            title={t("Clinical Synchronization")} 
            description={t("Clinical Synchronization Desc")}
          >
            <Accordion elevation={0} disableGutters sx={{ '&:before': { display: 'none' } }}>
              <AccordionSummary expandMoreIcon={<ExpandMoreIcon />}>
                <Typography sx={{ fontWeight: 700 }}>{t("Connecting a New Workstation")}</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.8 }}>
                  {t("Connecting a New Workstation Desc")}
                </Typography>
              </AccordionDetails>
            </Accordion>
          </HelpSection>

          {/* Section 6: Migration & Security */}
          <HelpSection 
            icon={<StorageIcon />} 
            title={t("System Maintenance")} 
            description={t("System Maintenance Desc")}
          >
            <Accordion elevation={0} disableGutters sx={{ '&:before': { display: 'none' } }}>
              <AccordionSummary expandMoreIcon={<ExpandMoreIcon />}>
                <Typography sx={{ fontWeight: 700 }}>{t("Automated Backups")}</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.8 }}>
                  {t("Automated Backups Desc")}
                </Typography>
              </AccordionDetails>
            </Accordion>
            <Accordion elevation={0} disableGutters sx={{ '&:before': { display: 'none' } }}>
              <AccordionSummary expandMoreIcon={<ExpandMoreIcon />}>
                <Typography sx={{ fontWeight: 700 }}>{t("Proxmox Migration")}</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.8 }}>
                  {t("Proxmox Migration Desc")}
                </Typography>
              </AccordionDetails>
            </Accordion>
          </HelpSection>
        </Grid>
      </Grid>
    </Container>
  );
};

export default HelpPage;
